import * as React from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "state/reducer/user";

import { ErrorPage } from "scenes/notfoundpage/index";
// import Recent from "scenes/recent/index";

import HTMLTag from "components/Reusable/HTMLTag";

// note : handle access
import {
  PreventDirectAccess,
  DirectAccess,
  DirectAccessByRegister,
} from "utils/access";
//Utils
import { RoleGuard } from "utils/roleGuard";

// note: auth scenes + layout stay eager (entry points, small)
import Register from "scenes/register/index";
import Logout from "scenes/logout/index";
import Login from "scenes/login/index";
import ForgotPassword from "scenes/forgot_password/index";
import ResetPassword from "scenes/reset_password/index";
import Main from "scenes/main";

//CASL
import { CASLProtectedRoute } from "components/CASL/CASLComponents";
import { ACTIONS, SUBJECTS } from "contexts/CASLContext";

// note: React.lazy for a named export
const lazyNamed = (factory, name) =>
  React.lazy(() => factory().then((m) => ({ default: m[name] })));

// note: page scenes are lazy -> each becomes its own chunk. Suspense lives in scenes/main.
const MyUnitsPage = lazyNamed(() => import("scenes/myunit"), "MyUnitsPage");
const MitraPage = lazyNamed(() => import("scenes/mitra"), "MitraPage");
const ServicesPage = React.lazy(() => import("scenes/service"));
const ProfilePage = React.lazy(() => import("scenes/profile"));
const UserPage = React.lazy(() => import("scenes/user"));
const RolePage = React.lazy(() => import("scenes/role"));
const CompanyManager = React.lazy(() => import("scenes/company/index2"));
const UnitPage = React.lazy(() => import("scenes/unit"));
const UnitDetailPage = React.lazy(() => import("scenes/unit/detail"));
const DashboardPage = React.lazy(() => import("scenes/dashboard"));
const HomePage = React.lazy(() => import("scenes/home"));
const BillingDashboard = React.lazy(() => import("scenes/billing_dashboard"));
const BillingCycles = React.lazy(() => import("scenes/billing_cycle"));
const BillingStatements = React.lazy(() => import("scenes/billing_statement"));
const BillingStatementDetail = React.lazy(() => import("scenes/billing_statement/BillingStatementDetail"));
const CreateBillingStatement = React.lazy(() => import("scenes/billing_statement/CreateStatement"));
const SalesRecapScene = React.lazy(() => import("scenes/sales_recap"));
const WarningLetterManagement = React.lazy(() => import("scenes/warning_letter"));
const PenaltyManagement = React.lazy(() => import("scenes/penalty"));
const UtilityRecordPage = React.lazy(() => import("scenes/utility_record"));
const ContractVersionsPage = React.lazy(() => import("scenes/contracts_new/ContractVersions"));
const OfferingPage = React.lazy(() => import("scenes/offering"));
const OfferingDetailPage = React.lazy(() => import("scenes/offering/OfferingDetail"));
const CreateOfferingPage = React.lazy(() => import("scenes/offering/CreateOffering"));
const PermissionPage = React.lazy(() => import("scenes/permission"));
const WorkingUnitPage = React.lazy(() => import("scenes/working_unit"));
const PropertyPage = React.lazy(() => import("scenes/property"));
const FloorPage = React.lazy(() => import("scenes/flor"));
const BuildingPage = React.lazy(() => import("scenes/building"));
const MyAccountPage = lazyNamed(() => import("scenes/account"), "MyAccountPage");
const CompanyPage = lazyNamed(() => import("scenes/company"), "CompanyPage");
const BasePricePage = lazyNamed(() => import("scenes/base_price"), "BasePricePage");
const InvoicePage = lazyNamed(() => import("scenes/invoices"), "InvoicePage");
const ContractsNewPage = lazyNamed(() => import("scenes/contracts_new"), "ContractsNewPage");
const ContractDetailPage = lazyNamed(() => import("scenes/contracts_new/ContractDetail"), "ContractDetailPage");
const PKSDetailPage = lazyNamed(() => import("scenes/contracts_new/PKSDetail"), "PKSDetailPage");
const ContractMonitoringPage = lazyNamed(() => import("scenes/contract_monitoring"), "ContractMonitoringPage");
const CostComponentPage = lazyNamed(() => import("scenes/cost_component"), "CostComponentPage");

// note: components used as pages
const BasePriceDetailPage = React.lazy(() => import("components/BasePrice"));
const RevenueDashboard = React.lazy(() => import("components/Dashboard/RevenueDashboard"));
const MitraManagement = React.lazy(() => import("components/Mitra/MitraManager"));
const BillingCycleDetail = React.lazy(() => import("components/Billing/DetailBillingCycle"));
const SalesRecapDetail = React.lazy(() => import("components/SalesRecap/SalesRecapDetail"));
const CreateSalesRecap = React.lazy(() => import("components/SalesRecap/CreateSalesRecap"));
const MitraDetailPage = lazyNamed(() => import("components/Mitra/DetailMitra"), "MitraDetailPage");
const DetailInvoicePage = lazyNamed(() => import("components/Invoice/DetailInvoice"), "DetailInvoicePage");
const CreateContractPage = lazyNamed(() => import("components/Contract/CreateContract"), "CreateContractPage");
const DetailContract = lazyNamed(() => import("components/Contract/DetailContract"), "DetailContract");

// note: delay for lazy
function delayForLazy(promise) {
  return new Promise((resolve) => {
    setTimeout(resolve, 500);
  }).then(() => promise);
}

// note: not found page
const NotFoundPage = React.lazy(() =>
  delayForLazy(import("scenes/notfoundpage/index.jsx")),
);

// note: INT users land on /home instead of the dashboard (login always redirects to /dashboard)
function DashboardOrHome() {
  const roleCode = useSelector(selectUser).getProfile?.data?.role?.code;
  return roleCode === "INT" ? <Navigate to="/home" replace /> : <DashboardPage />;
}

// note: routers
const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <HTMLTag>
        <PreventDirectAccess>
          <Main />
        </PreventDirectAccess>
      </HTMLTag>
    ),
    children: [
      {
        path: "/dashboard",
        element: <DashboardOrHome />,
      },
      {
        path: "/home",
        element: <HomePage />,
      },

      {
        path: "/billing/dashboard",
        element: <BillingDashboard />,
      },

      {
        path: "/billing/cycles",
        element: <BillingCycles />,
      },
      {
        path: "/billing/cycles/details",
        element: <BillingCycleDetail />,
      },
      {
        path: "/billing/statements",
        element: <BillingStatements />,
      },
      {
        path: "/billing/statements/create",
        element: <CreateBillingStatement />,
      },
      {
        path: "/billing/statements/:id",
        element: <BillingStatementDetail />,
      },

      // Sales Recap
      {
        path: "/sales-recap",
        element: <SalesRecapScene />,
      },
      {
        path: "/sales-recap/detail",
        element: <SalesRecapDetail />,
      },
      {
        path: "/sales-recap/create",
        element: <CreateSalesRecap />,
      },

      {
        path: "/user",
        element: <UserPage />,
      },

      {
        path: "/company",
        element: <CompanyPage />,
      },
      {
        path: "/base-price",
        element: <BasePricePage />,
      },
      {
        path: "/base-price/details",
        element: <BasePriceDetailPage />,
      },

      {
        path: "/services",
        element: <ServicesPage />,
      },

      {
        path: "/warning-letter",
        element: <WarningLetterManagement />,
      },
      {
        path: "/penalty",
        element: <PenaltyManagement />,
      },
      {
        path: "/utility-record",
        element: <UtilityRecordPage />,
      },
      {
        path: "/offering",
        element: <OfferingPage />,
      },
      {
        path: "/offering/create",
        element: <CreateOfferingPage />,
      },
      {
        path: "/offering/detail",
        element: <OfferingDetailPage />,
      },
      {
        path: "/contracts-management",
        element: <ContractsNewPage />,
      },
      {
        path: "/contracts-management/create",
        element: <CreateContractPage />,
      },
      {
        path: "/contracts-management/detail",
        element: <ContractDetailPage />,
      },
      {
        path: "/contracts-management/pks-detail",
        element: <PKSDetailPage />,
      },
      {
        path: "/contracts-management/versions",
        element: <ContractVersionsPage />,
      },
      {
        path: "/contracts-management/monitoring",
        element: <ContractMonitoringPage />,
      },

      {
        path: "/contract/detail",
        element: <DetailContract />,
      },

      {
        path: "/cost-components",
        element: <CostComponentPage />,
      },
      {
        path: "/property",
        element: <PropertyPage />,
      },
      {
        path: "/building",
        element: <BuildingPage />,
      },
      {
        path: "/floor",
        element: <FloorPage />,
      },
      {
        path: "/unit/mitra",
        element: <MyUnitsPage />,
      },
      {
        path: "/unit",
        element: <UnitPage />,
      },
      {
        path: "/unit/detail",
        element: <UnitDetailPage />,
      },

      {
        path: "/profile",
        element: <ProfilePage />,
      },
      {
        path: "/role",
        element: <RolePage />,
      },

      {
        path: "/work-unit",
        element: <WorkingUnitPage />,
      },
      {
        path: "/companny",
        element: <CompanyManager />,
      },
      {
        path: "/permissions",
        element: <PermissionPage />,
      },
      {
        path: "/roles",
        element: <RolePage />,
      },

      {
        path: "/mitras",
        element: (
          <CASLProtectedRoute action={ACTIONS.READ} subject={SUBJECTS.MITRA}>
            <MitraManagement />
          </CASLProtectedRoute>
        ),
      },
      {
        path: "/account",
        element: <MyAccountPage />,
      },
      {
        path: "/mitra",
        element: <MitraPage />,
      },

      {
        path: "/mitra/detail",
        element: <MitraDetailPage />,
      },
      {
        path: "/invoice",
        element: <InvoicePage />,
      },
      {
        path: "/invoice/detail",
        element: <DetailInvoicePage />,
      },

      {
        path: "/report",
        element: (
          <PreventDirectAccess>
            <RoleGuard
              allowedRoles={[
                "ADMIN",
                "ADMIN TENANT",
                "MITRA",
                "SPESIALIS",
                "SA",
                "STAFF",
              ]}
            >
              <RevenueDashboard />
            </RoleGuard>
          </PreventDirectAccess>
        ),
      },

      // {
      //   path: "/property/unit",
      //   element: <UserUnitPage />,
      // },
    ],
  },
  // note: login
  {
    path: "/login",
    element: (
      <HTMLTag>
        <DirectAccess>
          <Login />
        </DirectAccess>
      </HTMLTag>
    ),
  },
  // note: forgot password
  {
    path: "/forgot-password",
    element: (
      <HTMLTag>
        <DirectAccess>
          <ForgotPassword />
        </DirectAccess>
      </HTMLTag>
    ),
  },
  // note: reset password (link sent via email: /password/reset/:otp)
  {
    path: "/password/reset/:id",
    element: (
      <HTMLTag>
        <DirectAccess>
          <ResetPassword />
        </DirectAccess>
      </HTMLTag>
    ),
  },
  // note: register
  {
    path: "/register",
    element: (
      <HTMLTag>
        <DirectAccessByRegister>
          <Register />
        </DirectAccessByRegister>
      </HTMLTag>
    ),
  },
  // note: logout
  {
    path: "/logout",
    element: (
      <HTMLTag>
        <DirectAccess>
          <Logout />
        </DirectAccess>
      </HTMLTag>
    ),
  },
  // note: all path
  {
    path: "*",
    element: (
      <HTMLTag>
        <React.Suspense fallback={<ErrorPage />}>
          <NotFoundPage />
        </React.Suspense>
      </HTMLTag>
    ),
  },
]);

export default router;
